import { memo, useCallback } from 'react';
import classNames from 'classnames';
import { V2QsFrequencyProps, V2QsFrequencyOption } from './types';

// 기본 횟수 옵션
const DEFAULT_TIMES_OPTIONS: V2QsFrequencyOption[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
];

// 기본 기간 옵션
const DEFAULT_PERIOD_OPTIONS: V2QsFrequencyOption[] = [
  { value: 'day', label: 'day' },
  { value: 'week', label: 'week' },
  { value: 'month', label: 'month' },
];

/**
 * V2QsFrequency 컴포넌트
 *
 * 빈도 선택을 위한 컴포넌트 (횟수 + 기간)
 * V1 QsFrequencyInput의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * <V2QsFrequency
 *   value={{ times: '2', period: 'day' }}
 *   onChange={setValue}
 *   separator="per"
 * />
 * ```
 */
function V2QsFrequency({
  value,
  onChange,
  timesOptions = DEFAULT_TIMES_OPTIONS,
  periodOptions = DEFAULT_PERIOD_OPTIONS,
  separator = 'per',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  timesPlaceholder = '--',
  periodPlaceholder = '--',
}: V2QsFrequencyProps) {
  const { times = '', period = '' } = value || {};

  // 횟수 변경 핸들러
  const handleTimesChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.({
        times: e.target.value,
        period: period as string,
      });
    },
    [onChange, period]
  );

  // 기간 변경 핸들러
  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.({
        times: times as string,
        period: e.target.value,
      });
    },
    [onChange, times]
  );

  const hasError = error;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Wrapper 클래스
  const wrapperClasses = classNames(
    'flex items-center gap-3',
    {
      'w-full': fullWidth,
    },
    className
  );

  // Select 클래스
  const selectClasses = classNames(
    'flex-1 px-4 py-[14px] rounded-[10px] transition-all duration-200',
    'border-[1.5px] text-[15px] font-medium',
    'outline-none appearance-none bg-white',
    'bg-no-repeat bg-right',
    {
      // Border color
      'border-[#E6E9F5] focus:border-[#4B7BF5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,

      // Background
      'bg-[#F2F4FC]': disabled,

      // Text color
      'text-[#1D1D1F]': !disabled,
      'text-[#AFB2C2]': disabled,

      // Cursor
      'cursor-not-allowed': disabled,
      'cursor-pointer': !disabled && !readOnly,
    }
  );

  // Separator 클래스
  const separatorClasses = 'text-[15px] text-[#AFB2C2] font-medium';

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 커스텀 화살표 스타일
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23AFB2C2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundSize: '20px',
    backgroundPosition: 'right 12px center',
    paddingRight: '40px',
  };

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Select Wrapper */}
      <div className={wrapperClasses}>
        {/* Times Select */}
        <select
          value={times}
          onChange={handleTimesChange}
          disabled={disabled || readOnly}
          className={selectClasses}
          style={selectStyle}
        >
          <option value="">{timesPlaceholder}</option>
          {timesOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Separator */}
        <span className={separatorClasses}>{separator}</span>

        {/* Period Select */}
        <select
          value={period}
          onChange={handlePeriodChange}
          disabled={disabled || readOnly}
          className={selectClasses}
          style={selectStyle}
        >
          <option value="">{periodPlaceholder}</option>
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsFrequency);
